/*
 * Dates and numbers in the language the user chose.
 *
 * THE PROBLEM THIS SOLVES. `toLocaleString()` with no argument follows the BROWSER's locale,
 * not the product's. A Finn whose laptop is set to US English reads "Viimeksi nähty
 * 2/20/2024, 3:50:36 PM" — a Finnish sentence with an American date inside it. That is the
 * same mixing as an untranslated heading, and harder to spot, because nothing about it looks
 * like a missing translation.
 *
 * WHERE IT LIVES AND WHY. Registered on i18next's own interpolation rather than called from
 * each screen, so a translation file writes `{{time, datetime}}` and every date in the
 * product follows the selected language. A component cannot forget to do this, because it
 * never does it. Adding a language means adding one line to LOCALES.
 */

// The product ships three languages; each needs a real locale tag, since 'fi' alone is
// enough for Intl but 'en' would give whatever region the runtime guesses. en-GB rather than
// en-US: this is a European product, and 20/02/2024 is what its English-reading users expect.
const LOCALES = {
  fi: 'fi-FI',
  sv: 'sv-SE',
  en: 'en-GB',
};

const FALLBACK_LOCALE = LOCALES.en;

/*
 * A DATETIME NAMES ITS TIMEZONE; A DATE DOES NOT (OVIO-33).
 *
 * The zone follows the READER'S BROWSER, and deliberately still does: a position fix is an
 * instant, not a business date, and "how long ago was this" is answered in the clock of
 * whoever is asking. What was missing is that nothing said WHICH zone, so two managers in
 * different offices read different numbers off the same fix with nothing on screen to
 * reconcile them.
 *
 * `timeZone` is intentionally absent — naming one here would pin every reader to somebody
 * else's clock. Only `timeZoneName` is set.
 *
 * The components are spelled out because `timeZoneName` cannot be combined with `dateStyle`,
 * and they are chosen to reproduce `toLocaleString()` exactly: for all three shipped locales
 * the rendering is byte-identical to what it was, plus the zone. A test pins that, because
 * "make room for the zone" is exactly the edit that quietly drops the seconds.
 *
 * 'short' rather than 'long' (operator, 2026-08-15): compact enough for a table cell, at the
 * cost of reading as an offset in Finnish (UTC+3) and an abbreviation in Swedish and English
 * (EEST).
 */
const DATETIME_PARTS = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
};

// A CLOCK TIME INSIDE A DAY THE READER ALREADY CHOSE. A journey table sits under a date
// picker, so repeating the date in every row is noise -- and so is repeating `UTC+3` in
// every cell of a table whose rows all share it (operator, 2026-08-30).
//
// THE ZONE IS NOT DROPPED, IT IS MOVED. Two managers in different offices still have to
// reconcile the same row, so the caller says the zone ONCE for the table and marks any row
// that disagrees with it -- which is the only case where a per-cell suffix carried
// information. `zoneNameFor` below is how a caller gets that string.
const TIME_PARTS = {
  hour: '2-digit',
  minute: '2-digit',
};

/** The locale tag for an i18next language, which may arrive bare ('fi') or regional ('fi-FI'). */
function localeFor(language) {
  if (!language) {
    return FALLBACK_LOCALE;
  }
  const base = String(language).toLowerCase().split('-')[0];
  return LOCALES[base] || FALLBACK_LOCALE;
}

/**
 * i18next's interpolation formatter.
 *
 * Called for EVERY interpolated value in the product, so it must be inert unless a format was
 * asked for: the hundreds of existing `{{name}}` placeholders pass no format spec and have to
 * come back untouched.
 */
function formatValue(value, format, language) {
  // A NUMBER IS A DATE'S TWIN PROBLEM. An odometer reads six figures, and `245679` against
  // `24567` is one glyph and an order of magnitude apart -- which is the difference between
  // a service due now and one due next year. Elsewhere the product writes
  // `toLocaleString('fi-FI')` at the call site; the point of this module is that it does not
  // have to. Anything that is not a finite number comes back untouched, so a missing reading
  // renders as whatever the caller had rather than as `NaN`.
  if (format === 'number') {
    return (typeof value === 'number' && isFinite(value))
      ? value.toLocaleString(localeFor(language))
      : value;
  }
  if (format !== 'datetime' && format !== 'date' && format !== 'time') {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return value;
  }
  const when = value instanceof Date ? value : new Date(value);
  // An unusable timestamp returns the original rather than the string "Invalid Date". A
  // fleet manager reading "Viimeksi nähty Invalid Date" learns nothing and distrusts the
  // rest of the screen.
  if (isNaN(when.getTime())) {
    return value;
  }
  const locale = localeFor(language);
  // A plain date carries no time, so it carries no zone: the day is the same day whoever
  // reads it, and a suffix there would be noise.
  if (format === 'date') {
    return when.toLocaleDateString(locale);
  }
  return new Intl.DateTimeFormat(locale, format === 'time' ? TIME_PARTS : DATETIME_PARTS)
    .format(when);
}

/**
 * The short timezone name for an instant, in the chosen language -- 'UTC+3' in Finnish,
 * 'EEST' in English and Swedish.
 *
 * Separated from the time itself so a table can state its zone once instead of in every
 * cell. Returns null for anything it cannot read, so a caller can say nothing rather than
 * something wrong.
 */
function zoneNameFor(value, language) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const when = value instanceof Date ? value : new Date(value);
  if (isNaN(when.getTime())) {
    return null;
  }
  const parts = new Intl.DateTimeFormat(localeFor(language), {
    hour: '2-digit',
    timeZoneName: 'short',
  }).formatToParts(when);
  const zone = parts.find((part) => part.type === 'timeZoneName');
  return zone ? zone.value : null;
}

export { localeFor, formatValue, zoneNameFor, LOCALES };
